import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { pickInfoPriority } from '../consts';
import {
    BitmapInfo,
    isMVTLayer,
    isPlumeProperties,
    isRasterPointProperties,
    MSatPickInfo,
    PlumeEmissionPickInfo
} from '../types';

/**
 * Gets the prefix of the id if it is part of pickInfoPriority. This is because some
 * ids have an added suffix appended to the root of the id.
 *
 * @example
 * getRootId('area-emission-raster-uinta')
 * // returns 'area-emission-raster'
 */
export const getRootId = (id: string) => {
    const rootId = pickInfoPriority.find((pickInfoId) => id.startsWith(pickInfoId.toString()));
    return rootId ?? id;
};

/**
 * Gets the id of an object from a MSatPickInfo object.
 * Returns `feature.properties.<uniqueIdProperty>` or `feature.id`
 */
export function getFeatureIdFromPickInfo(info: MSatPickInfo) {
    const feature = info.object;

    if (feature.properties && isMVTLayer(info.layer) && info.layer.props.uniqueIdProperty) {
        const uniqueIdProperty = info.layer.props.uniqueIdProperty;
        return feature.properties[uniqueIdProperty];
    }

    if (feature.properties && 'uniqueIdProperty' in info.layer && info.layer.uniqueIdProperty) {
        const uniqueIdProperty = info.layer.uniqueIdProperty;
        return feature.properties[uniqueIdProperty];
    }

    if ('id' in feature) {
        return feature.id;
    }

    return undefined;
}

/**
 * Gets the id of a raster object (`BitmapInfo`), plume feature or other geojson feature.
 */
export function getFeatureIdFromFeature(feature: Feature<Geometry, GeoJsonProperties> | BitmapInfo) {
    if (!('properties' in feature) || !feature.properties) return undefined;

    if (isRasterPointProperties(feature.properties)) {
        return feature.id;
    }
    if (isPlumeProperties(feature.properties)) {
        return feature.properties.plume_id;
    }

    if ('id' in feature) {
        return feature.id;
    }

    return undefined;
}

/** Returns a unique ID used for highlighting plumes on hover */
export const getHoveredPlumeId = (hoveredPlume: PlumeEmissionPickInfo['object'] | undefined) => {
    if (!hoveredPlume) return null;
    return `${hoveredPlume.properties?.plume_id}-${hoveredPlume?.parentTargetId}`;
};
